
/**
 * User Integration Layer
 * Connects SDK Service to Zustand Store
 */
import i18next from 'i18next';
import { toast } from 'sonner';
import { fetchDropdownOptionsFromReference } from '@/lib/reference-dropdown.helper.js';
import { useTenantStore } from '@/store/tenant.store.js';
import { useUserStore } from '../../store/user.store.js';
import { Logger } from '@/lib/app-log.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class UserIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * Fetch all items and update store
     */
    async fetchUsers(arg1 = 1, arg2 = 10, arg3 = '', arg4 = '', arg5 = 'desc', arg6 = {}) {
        let page = 1, pageSize = 10, search = '', sortBy = '', sortOrder = 'desc', columnFilters = {};

        let roleId;
        if (typeof arg1 === 'object' && arg1 !== null) {
            // Backward compatibility for object params
            page = arg1.page || 1;
            pageSize = arg1.pageSize || 10;
            search = arg1.search || '';
            sortBy = arg1.sortBy || '';
            sortOrder = arg1.sortOrder || (arg1.sortDirection === 'asc' ? 'asc' : 'desc');
            columnFilters = arg1.columnFilters || {};
            roleId = arg1.roleId;
        } else {
            // Positional arguments
            page = arg1;
            pageSize = arg2;
            search = arg3;
            sortBy = arg4;
            sortOrder = arg5;
            columnFilters = arg6;
        }

        const store = useUserStore.getState();

        if (store.setLoading) store.setLoading(true);
        if (store.clearError) store.clearError();

        try {
            const params = {
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                columnFilters
            };
            if (roleId) params.roleId = roleId;

            const response = await this.service.getAll(params);
            const { data, pagination, uniqueValues } = response;

            store.setUsers(data || []);

            if (pagination) {
                store.setPaginationData({
                    currentPage: pagination.page || page,
                    totalPages: pagination.totalPages || 1,
                    totalItems: pagination.total || 0,
                    itemsPerPage: pagination.pageSize || pageSize,
                    hasNextPage: pagination.page < pagination.totalPages,
                    hasPreviousPage: pagination.page > 1,
                });
            }

            if (uniqueValues) {
                store.setUniqueValues(uniqueValues);
            }

            if (store.setSearchParams) {
                store.setSearchParams({
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    columnFilters,
                });
            }

            return { success: true, data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (store.setError) store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Fetch users for dropdown
     */
    async fetchUsersForDropdown(options = {}) {
        const store = useUserStore.getState();
        const requestTenantId = options.tenantId || useTenantStore.getState().selectedTenantId;
        try {
            const result = await fetchDropdownOptionsFromReference('user', async () => {
                if (this.service.getDropdownParams) {
                    const response = await this.service.getDropdownParams();
                    return { success: true, data: response.data || [] };
                }
                const response = await this.service.getAll({ pageSize: 1000 });
                const dropdownData = (response.data || []).map(u => {
                    const label = (u.firstName || u.lastName)
                        ? `${u.firstName || ''} ${u.lastName || ''}`.trim()
                        : (u.name || u.userName || u.email || '');
                    return {
                        value: u._id || u.id,
                        label
                    };
                });
                return { success: true, data: dropdownData };
            }, options);
            if (String(useTenantStore.getState().selectedTenantId || '') !== String(requestTenantId || '')) return;
            store.setUsersForDropdown(result.data || []);
        } catch (error) {
            Logger.warn('Failed to fetch user dropdown', { error: error?.message });
        }
    }

    async downloadExport() {
        const store = useUserStore.getState();
        const sp = store.searchParams || {};
        try {
            const { data: blob } = await this.service.exportCsv({
                page: 1,
                pageSize: 5000,
                search: sp.search || '',
                sortBy: sp.sortBy || 'createdAt',
                sortOrder: sp.sortOrder || 'desc',
                columnFilters: sp.columnFilters || {},
                maxRows: 5000,
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'users-export.csv';
            a.click();
            URL.revokeObjectURL(url);
            toast.success(i18next.t('integrationToasts:exportDownloaded', { defaultValue: 'Export downloaded' }));
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async inviteUser(payload) {
        const store = useUserStore.getState();
        if (store.setLoading) store.setLoading(true);
        try {
            const res = await this.service.inviteUser(payload);
            toast.success(i18next.t('integrationToasts:invitationCreated', { defaultValue: 'Invitation created' }));
            return { success: true, data: res.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    async resendInvite(userId) {
        try {
            const res = await this.service.resendInvite(userId);
            toast.success(i18next.t('integrationToasts:activationLinkRefreshed', { defaultValue: 'Activation link refreshed' }));
            return { success: true, data: res.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async batchPatchUsers(payload) {
        const store = useUserStore.getState();
        if (store.setLoading) store.setLoading(true);
        try {
            const res = await this.service.batchPatchUsers(payload);
            toast.success(i18next.t('integrationToasts:usersUpdated', { defaultValue: 'Users updated' }));
            return { success: true, data: res.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Get by ID
     */
    async fetchUserById(id) {
        const store = useUserStore.getState();
        if (store.setLoading) store.setLoading(true);

        try {
            const response = await this.service.getById(id);

            // Try common naming for selecting single item
            if (store.setSelectedUser) {
                store.setSelectedUser(response.data);
            } else if (store.setCurrentUser) {
                store.setCurrentUser(response.data);
            } else if (store.setSelectedItem) {
                store.setSelectedItem(response.data);
            }

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Create item
     */
    async createUser(data) {
        const store = useUserStore.getState();
        if (store.setLoading) store.setLoading(true);

        try {
            const response = await this.service.create(data);

            toast.success(i18next.t('integrationToasts:userCreated', { defaultValue: 'User created' }));

            // Optional: Close form if store has that action
            if (store.closeForm) store.closeForm();
            if (store.cancelEditing) store.cancelEditing();

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            // 409 unit-role conflicts are handled by the form, suppress generic toast.
            if (error?.statusCode !== 409) {
                toast.error(message);
            }
            return {
                success: false,
                error: message,
                statusCode: error?.statusCode,
                field: this._getErrorField(error),
                conflicts: error?.conflicts,
            };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Update item
     */
    async updateUser(id, data) {
        const store = useUserStore.getState();
        if (store.setLoading) store.setLoading(true);

        try {
            const response = await this.service.update(id, data);

            toast.success(i18next.t('integrationToasts:userUpdated', { defaultValue: 'User updated' }));

            if (store.closeForm) store.closeForm();
            if (store.cancelEditing) store.cancelEditing();

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (error?.statusCode !== 409) {
                toast.error(message);
            }
            return {
                success: false,
                error: message,
                statusCode: error?.statusCode,
                field: this._getErrorField(error),
                conflicts: error?.conflicts,
            };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Delete item
     */
    async deleteUser(id) {
        const store = useUserStore.getState();
        if (store.setLoading) store.setLoading(true);

        try {
            await this.service.delete(id);

            toast.success(i18next.t('integrationToasts:userDeleted', { defaultValue: 'User deleted' }));

            if (store.closeDeleteDialog) store.closeDeleteDialog();

            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * List users assigned to a role (does not replace global user store).
     */
    async fetchUsersByRole(roleId, options = {}) {
        try {
            const response = await this.service.getAll({
                page: options.page || 1,
                pageSize: options.pageSize || 15,
                roleId,
                search: options.search || '',
                sortBy: options.sortBy || '',
                sortOrder: options.sortOrder || 'desc',
                columnFilters: options.columnFilters || {},
            });
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
            };
        } catch (error) {
            return {
                success: false,
                error: this._getErrorMessage(error),
                data: [],
                pagination: {},
            };
        }
    }

    /**
     * List users assigned to a unit (does not replace global user store).
     */
    async fetchUsersByUnit(unitId, options = {}) {
        const id = unitId ? String(unitId) : '';
        if (!id) {
            return { success: false, error: 'Unit id required', data: [], pagination: {} };
        }
        try {
            const params = {
                page: options.page || 1,
                pageSize: options.pageSize || 15,
                unitId: id,
                sortOrder: options.sortOrder || 'desc',
            };
            if (options.search) params.search = options.search;
            if (options.sortBy) params.sortBy = options.sortBy;
            if (options.columnFilters && Object.keys(options.columnFilters).length > 0) {
                params.columnFilters = options.columnFilters;
            }
            const response = await this.service.getAll(params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
            };
        } catch (error) {
            return {
                success: false,
                error: this._getErrorMessage(error),
                data: [],
                pagination: {},
            };
        }
    }

    /**
     * List users without mutating the global user store (e.g. role assignment picker).
     */
    async fetchUsersList(options = {}) {
        try {
            const params = {
                page: options.page || 1,
                pageSize: options.pageSize || 100,
                search: options.search || '',
                sortBy: options.sortBy || 'createdAt',
                sortOrder: options.sortOrder || 'desc',
                columnFilters: options.columnFilters || {},
            };
            if (options.excludeRoleId) {
                params.excludeRoleId = options.excludeRoleId;
            }
            if (options.noUnit) {
                params.noUnit = options.noUnit;
            }
            const response = await this.service.getAll(params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
            };
        } catch (error) {
            return {
                success: false,
                error: this._getErrorMessage(error),
                data: [],
                pagination: {},
            };
        }
    }

    /**
     * Fetch unit-role assignments for a given user (used in User form to show
     * which (unit, role) tuples are currently linked to this user).
     */
    async fetchUnitRoleAssignments(userId) {
        try {
            const result = await this.service.getUnitRoleAssignments(userId);
            return { success: true, data: result.data || [] };
        } catch (error) {
            return {
                success: false,
                error: this._getErrorMessage(error),
                data: [],
            };
        }
    }

    /**
     * Bulk delete items
     */
    async bulkDeleteUsers(ids) {
        const store = useUserStore.getState();
        if (store.setLoading) store.setLoading(true);

        try {
            await this.service.bulkDelete(ids);
            toast.success(i18next.t('integrationToasts:selectedUsersDeleted', { defaultValue: 'Selected users deleted' }));
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }


    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }

    /**
     * Infer which form field an API error refers to (duplicate username/email, etc.).
     */
    _getErrorField(error) {
        const details = typeof error?.details === 'string'
            ? error.details
            : (typeof error?.details?.details === 'string' ? error.details.details : '');
        const message = String(error?.message || '');
        const haystack = `${message} ${details}`;
        if (/userName|kullanıcı adı/i.test(haystack)) return 'userName';
        if (/email|e-posta/i.test(haystack)) return 'email';
        return null;
    }
}
